<template>
  <div class="bulk-rate-change">

    <!-- Filter form -->
    <el-form label-position="top" class="bulk-rate-change__form">
      <el-row :gutter="16">

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Telco" class="bulk-rate-change__telco">
            <!-- :key remounts the select on Reset. SelectSharer/SelectOrg snapshot
                 `value` into internal formData at create-time and don't watch it, so
                 setting telcoId=null in onReset wouldn't clear the displayed telco;
                 bumping the key re-creates it with the (now null) value. -->
            <select-sharer
              :key="telcoSelectKey"
              v-model="telcoId"
              label=""
              :no-form="true"
              placeholder="Select a telco…" />
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Current sharer rate">
            <select-sharer-rates
              v-model="filterRateId"
              :sharer-id="telcoId"
              :no-form="true"
              clearable
              :disabled="!telcoId"
              placeholder="Choose a rate…" />
            <p v-if="!telcoId" class="text-muted" style="font-size: 12px; margin: 0;">Pick a telco first.</p>
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Rate version">
            <select-rate-versions
              :versions="filterRateVersions"
              :value="filterVersionId"
              :loading="loadingFilterVersions"
              :disabled="!filterRateId"
              clearable
              placeholder="Any version"
              @input="onFilterVersionInput" />
            <p v-if="!filterRateId" class="text-muted" style="font-size: 12px; margin: 0;">Pick a rate first.</p>
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Site ID">
            <el-input
              v-model="site"
              clearable
              placeholder="Site ID or code" />
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Structure type(s)">
            <el-select
              v-model="structureTypes"
              multiple
              collapse-tags
              clearable
              filterable
              :loading="loadingStructureTypes"
              loading-text="Loading structure types…"
              no-data-text="No structure types available"
              placeholder="Any type"
              style="width: 100%">
              <el-option
                v-for="opt in structureTypeOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value" />
            </el-select>
            <p
              v-if="!loadingStructureTypes && structureTypeOptions.length === 0"
              class="text-muted"
              style="font-size: 12px; margin: 0;">
              Structure type filter unavailable — search works without it.
            </p>
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Height range">
            <div class="bulk-rate-change__range-row">
              <el-input-number
                v-model="heightMin"
                :min="0"
                controls-position="right"
                aria-label="Minimum height"
                placeholder="Min"
                style="width: 100%" />
              <span class="bulk-rate-change__range-sep">to</span>
              <el-input-number
                v-model="heightMax"
                :min="0"
                controls-position="right"
                aria-label="Maximum height"
                placeholder="Max"
                style="width: 100%" />
            </div>
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Handover range">
            <el-date-picker
              v-model="handoverRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              unlink-panels
              start-placeholder="Handover from"
              end-placeholder="Handover to"
              style="width: 100%" />
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Start range">
            <el-date-picker
              v-model="startedRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              unlink-panels
              start-placeholder="Start from"
              end-placeholder="Start to"
              style="width: 100%" />
          </el-form-item>
        </el-col>

        <el-col :md="8" :sm="12" :xs="24">
          <el-form-item label="Rate status">
            <el-checkbox v-model="rateOverridden">Rate overridden</el-checkbox>
            <el-checkbox v-model="noRate">No rate</el-checkbox>
          </el-form-item>
        </el-col>

        <el-col :span="24" style="text-align: right">
          <el-button
            type="primary"
            :disabled="!telcoId"
            @click="onSearch">
            Search
          </el-button>
          <el-button @click="onReset">Reset</el-button>
        </el-col>

      </el-row>
    </el-form>

    <!-- Post-apply resuming banner -->
    <el-alert
      v-if="resuming"
      type="info"
      :closable="false"
      show-icon
      title=""
      description="Fees recalculating in the background — this may take a moment."
      style="margin-bottom: 12px" />

    <!-- Bulk rate-assign bar: visible when rows are selected OR select-all mode is active -->
    <bulk-rate-assign
      v-if="selectedRows.length || selectAllMatching"
      :licensee-rows="selectedRows"
      :organisation-id="telcoId"
      :match-count="matchTotal"
      :select-all-matching="selectAllMatching"
      :filter="searchParams"
      :excluded-ids="excludedIds"
      @applied="onApplied" />

    <!-- Select-all banner: offer state (page fully selected, more pages exist)
         or active state (select-all-matching is engaged).
         Rendered between the apply bar and the results table.
         role="status" + aria-live="polite" so screen readers announce mode changes.
         In active state (selectAllMatching), the summary is a single computed
         text node so the whole sentence is read on change (a11y: single live region). -->
    <el-alert
      v-if="showSelectAllBanner || selectAllMatching"
      :type="selectAllMatching ? 'warning' : 'info'"
      :closable="false"
      show-icon
      class="bulk-rate-change__selectall"
      :class="{ 'bulk-rate-change__selectall--active': selectAllMatching }"
      role="status"
      aria-live="polite"
      style="margin-bottom: 12px">
      <template v-if="!selectAllMatching">
        <span>All {{ selectedRows.length }} on this page are selected.&nbsp;</span>
        <el-button type="text" @click="enterSelectAllMatching">Select all {{ matchTotal }} matching</el-button>
      </template>
      <template v-else>
        <span>{{ selectAllSummary }}</span>
        <span class="bulk-rate-change__banner-sep" aria-hidden="true">&nbsp;·&nbsp;</span>
        <el-button type="text" @click="clearSelectAllMatching">Clear selection</el-button>
        <template v-if="excludedIds.length > 0">
          <span class="bulk-rate-change__banner-sep" aria-hidden="true">&nbsp;·&nbsp;</span>
          <el-button type="text" @click="resetExclusions">Reset exclusions</el-button>
        </template>
      </template>
    </el-alert>

    <!-- Results table (only after first search with a telco).
         :key="searchKey" forces a fresh mount on each Search so XolasTable
         re-reads searchParams into its internal moreParams (it snapshots params
         at create-time and only rebuilds them on its own filter events, not when
         the :params prop changes) — without this, changing a filter and clicking
         Search again would re-render the old result set. -->
    <xolas-table
      v-if="searched && telcoId"
      :key="searchKey"
      ref="resultsTable"
      :selectable="true"
      :auto-select-all="selectAllMatching"
      :excluded-ids="excludedIds"
      url="/licensees/search"
      table-ref="bulkratetable"
      track-by="licensee_id"
      :params="searchParams"
      :fields="fields"
      :show-search="false"
      @total-changed="onTotalChanged"
      @selection-change="onSelectionChange">

      <template #site="props">
        <div class="audit-site-cell">
          <span class="audit-site-cell__id">{{ props.rowData.site && props.rowData.site.siteId }}</span>
          <span class="audit-site-cell__name">{{ props.rowData.site && props.rowData.site.siteName }}</span>
        </div>
      </template>

      <template #currentRate="props">
        <!-- Manual override takes precedence over the sharer rate: a locked fee is
             what actually bills, so show "Rate overridden" + the set amount instead
             of the rate name/version. (override_rate comes from the search row when
             rate_locked; until the API adds it the amount line is simply omitted.) -->
        <template v-if="props.rowData.rate_locked">
          <div class="bulk-rate-change__override-tag-row">
            <el-tag type="warning" size="mini" effect="light" class="bulk-rate-change__override-tag">Rate overridden</el-tag>
          </div>
          <div
            v-if="props.rowData.override_rate !== null && props.rowData.override_rate !== undefined"
            class="bulk-rate-change__rate-name">
            {{ formatMoney(props.rowData.override_rate, 2) }}
          </div>
        </template>
        <template v-else-if="props.rowData.rate && props.rowData.rate.name">
          <div class="bulk-rate-change__rate-name">
            <el-link
              type="primary"
              :underline="false"
              :aria-label="'View tier rates for ' + props.rowData.rate.name"
              @click="openRateVersion(props.rowData)">
              {{ props.rowData.rate.name }}<i class="el-icon-tickets bulk-rate-change__rate-icon" aria-hidden="true" />
            </el-link>
          </div>
          <div class="bulk-rate-change__rate-version">
            <el-tag
              v-if="props.rowData.version_no"
              :type="props.rowData.is_current ? 'info' : 'warning'"
              size="mini"
              effect="light">
              v{{ props.rowData.version_no }}<span v-if="!props.rowData.is_current"> · superseded</span>
            </el-tag>
            <span v-else class="bulk-rate-change__rate-muted">Live (unpinned)</span>
          </div>
        </template>
        <el-tag v-else type="danger" size="small" effect="light">No rate</el-tag>
      </template>

      <template #empty>
        <div class="bulk-rate-change__empty">
          <p class="bulk-rate-change__empty-text">No site-licensees match these filters.</p>
          <p class="bulk-rate-change__empty-muted">Adjust the filters and search again.</p>
        </div>
      </template>

    </xolas-table>

    <!-- Pre-search prompt (before first search or after reset). Hand-rolled:
         Element UI 2.x does NOT ship <el-empty> (that's Element Plus / Vue 3) —
         same pattern as the table's #empty slot above and SitesTable/RequestTable. -->
    <div v-else-if="!searched || !telcoId" class="bulk-rate-change__empty">
      <i class="fa fa-inbox bulk-rate-change__empty-icon" aria-hidden="true" />
      <p class="bulk-rate-change__empty-muted">Pick a telco and Search to begin.</p>
    </div>

    <!-- Tier-rates info dialog — one shared instance, opened per row click -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="dialogTitle"
      width="80%"
      :close-on-click-modal="true"
      class="text-left"
      append-to-body
      @close="onDialogClose">

      <div class="bulk-rate-change__dialog-body">
        <sharer-rate-version
          v-if="dialogVisible && activeRow"
          :key="activeRow.licensee_id"
          :id="activeRow.licensee_id"
          :rate-id="activeRow.rate && activeRow.rate.id"
          unpinned-fallback />
      </div>

      <template #footer>
        <div style="text-align: right">
          <el-button @click="dialogVisible = false">Close</el-button>
        </div>
      </template>

    </el-dialog>

  </div>
</template>

<script>

import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'

import XolasTable from '@/components/atoms/tables/XolasTable'
import SelectSharer from '@/components/atoms/selects/SelectSharer'
import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'
import SelectRateVersions from '@/components/atoms/selects/SelectRateVersions'
import BulkRateAssign from '@/components/finance/rates/BulkRateAssign'
import SharerRateVersion from '@/components/sites/finance/sharer/SharerRateVersion'

export default {
  name: 'bulk-rate-change',

  mixins: [PostMixins, NumberMixins, DateMixins],

  components: {
    XolasTable,
    SelectSharer,
    SelectSharerRates,
    SelectRateVersions,
    BulkRateAssign,
    SharerRateVersion
  },

  created: function () {
    var self = this
    self.loadingStructureTypes = true
    this.fetchData('structures/names', function (data) {
      self.structureTypeOptions = Array.isArray(data) ? data : []
      self.loadingStructureTypes = false
    }, 'structure types', function () {
      // Error: degrade gracefully — empty picker, search still works.
      // fetchData already shows an error toast with the "structure types" label.
      self.structureTypeOptions = []
      self.loadingStructureTypes = false
    })
  },

  computed: {
    // XOL-321 Phase B — effective selected count for display and apply-bar.
    // In select-all mode: matchTotal minus the number of explicitly excluded rows;
    // guarded to never go below 0 even if the count is somehow off.
    // In explicit (fan-out) mode: the length of the checked rows array.
    effectiveSelectedCount: function () {
      if (this.selectAllMatching) {
        return Math.max(0, this.matchTotal - this.excludedIds.length)
      }
      return this.selectedRows.length
    },

    // XOL-321 Phase B — banner summary text (single computed string so the
    // entire sentence is read as one unit by screen readers via the aria-live
    // polite region — do NOT split into multiple nodes).
    selectAllSummary: function () {
      var N = this.matchTotal
      var K = this.excludedIds.length
      var nounN = N === 1 ? 'licensee' : 'licensees'
      if (K === 0) {
        return 'All ' + N + ' matching ' + nounN + ' selected.'
      }
      var M = Math.max(0, N - K)
      return M + ' of ' + N + ' matching ' + nounN + ' selected · ' + K + ' excluded.'
    },

    // Dialog title — site-qualified when activeRow carries site.siteId.
    dialogTitle: function () {
      if (!this.activeRow) return 'Tier rates'
      var rate = this.activeRow.rate
      var rateName = (rate && rate.name) ? rate.name : ''
      var siteId = (this.activeRow.site && this.activeRow.site.siteId)
        ? this.activeRow.site.siteId
        : ''
      return 'Tier rates — ' + (siteId ? siteId + ' · ' : '') + rateName
    },

    // Serialize filter state into XolasTable :params — omit blank values so the
    // API doesn't receive empty strings / null query params.
    searchParams: function () {
      var params = {}
      if (this.telcoId) params.organisation_id = this.telcoId
      if (this.site && this.site.trim()) params.site = this.site.trim()
      // Plain key — axios 0.15.3 auto-appends `[]` to array values, producing
      // `structure_type[]=a&structure_type[]=b` (Laravel reads it as an array).
      // Using the bracketed key here would double-bracket → `structure_type[][]=`.
      if (this.structureTypes && this.structureTypes.length) {
        params.structure_type = this.structureTypes
      }
      // Truthy check: el-input-number coerces an empty value to 0, and a 0 height
      // bound is meaningless (it would filter every structure out) — so omit 0/null.
      if (this.heightMin) params.height_min = this.heightMin
      if (this.heightMax) params.height_max = this.heightMax
      if (this.handoverRange && this.handoverRange[0]) params.handover_from = this.handoverRange[0]
      if (this.handoverRange && this.handoverRange[1]) params.handover_to = this.handoverRange[1]
      if (this.startedRange && this.startedRange[0]) params.started_from = this.startedRange[0]
      if (this.startedRange && this.startedRange[1]) params.started_to = this.startedRange[1]
      // Rate/version filters — serialize when present; no-op server-side until
      // GET /licensees/search accepts them (brave-kestrel ASK 1).
      if (this.filterRateId) params.rate_id = this.filterRateId
      if (this.filterVersionId) params.rate_version_id = this.filterVersionId
      // Override-set filter: checked → only manually-overridden licensees
      // (API `rate_locked=1` → licensees.rateLocked = 1). Unchecked → omit (both).
      if (this.rateOverridden) params.rate_locked = 1
      // No-rate filter: checked → only unrated licensees (API `no_rate=1` →
      // licensees.rate_id IS NULL). Unchecked → omit. No-op server-side until
      // GET /licensees/search accepts it (brave-kestrel).
      if (this.noRate) params.no_rate = 1
      return params
    }
  },

  methods: {
    formatHeight: function (value) {
      if (value === null || value === undefined || value === '') return '—'
      // vw_sharers.structureHeight can arrive as a string (e.g. "45.00") — coerce
      // so it renders "45m" not "45.00m".
      var n = Number(value)
      return (isNaN(n) ? value : n) + 'm'
    },

    onSearch: function () {
      if (!this.telcoId) return
      // Clear any existing selection before a new search, including select-all mode
      this.selectedRows = []
      this.selectAllMatching = false
      this.showSelectAllBanner = false
      this.matchTotal = 0
      this.excludedIds = []
      if (this.$refs.resultsTable) {
        this.$refs.resultsTable.clearSelection()
      }
      this.searched = true
      // Bump the table key so it remounts and re-reads the current searchParams
      // (XolasTable freezes its params at create-time — see the :key note above).
      this.searchKey++
    },

    onReset: function () {
      this.telcoId = null
      // Remount the telco select so it visually clears (it doesn't watch `value`).
      this.telcoSelectKey++
      this.site = ''
      this.structureTypes = []
      this.heightMin = null
      this.heightMax = null
      this.handoverRange = []
      this.startedRange = []
      this.filterRateId = null
      this.filterVersionId = null
      this.filterRateVersions = []
      this.rateOverridden = false
      this.noRate = false
      this.searched = false
      this.selectedRows = []
      this.selectAllMatching = false
      this.showSelectAllBanner = false
      this.matchTotal = 0
      this.excludedIds = []
    },

    onSelectionChange: function (rows) {
      // ── Select-all mode: page-scope reconcile ────────────────────────────
      // In select-all mode this event fires only from genuine user interaction
      // (the suppression guard in XolasTable ensures autoSelectAll batch calls
      // never reach here). Reconcile only the current-page ids — never treat
      // "every matched id not in the event" as excluded (that would wrongly
      // exclude rows on other pages).
      if (this.selectAllMatching) {
        var table = this.$refs.resultsTable
        var pageIds = table ? table.currentRowIds() : []
        // Build a fast lookup of which ids are checked in this event
        var checkedSet = {}
        rows.forEach(function (r) { checkedSet[r.licensee_id] = true })
        // Produce a NEW excludedIds array so the prop-watcher on XolasTable fires.
        // Drop ALL current-page ids first (clears any prior exclusion for re-checked
        // rows); other-page exclusions stay intact. Then re-add only the page ids
        // that are now un-checked. Net: this page's exclusions reflect the event,
        // off-page exclusions are untouched.
        var newExcluded = this.excludedIds.filter(function (id) {
          return pageIds.indexOf(id) === -1
        })
        pageIds.forEach(function (id) {
          if (!checkedSet[id]) newExcluded.push(id)
        })
        this.excludedIds = newExcluded
        // Collapse: if every matched licensee is excluded, exit select-all
        if (this.excludedIds.length >= this.matchTotal) {
          this.clearSelectAllMatching()
        }
        // Do NOT collapse selectAllMatching on partial un-check — banner stays.
        return
      }

      // ── Explicit (fan-out) mode ───────────────────────────────────────────
      this.selectedRows = rows
      // Detect page-fully-selected: when every row on the current page is checked
      // and there are more pages, offer the "select all N matching" banner.
      if (this.$refs.resultsTable) {
        var pageCount = this.$refs.resultsTable.currentRowCount()
        var pageFullySelected = rows.length > 0 && rows.length === pageCount
        var hasMorePages = this.matchTotal > rows.length
        if (pageFullySelected && hasMorePages) {
          this.showSelectAllBanner = true
        } else if (!pageFullySelected) {
          // Un-check: clear banner and exit select-all mode
          this.showSelectAllBanner = false
          this.selectAllMatching = false
        }
      }
    },

    onApplied: function () {
      // Refresh the table so changed rates are visible
      this.$events.emit('bulkratetable-refresh-table')
      // Clear the current selection and any select-all mode
      if (this.$refs.resultsTable) {
        this.$refs.resultsTable.clearSelection()
      }
      this.selectedRows = []
      this.selectAllMatching = false
      this.showSelectAllBanner = false
      this.excludedIds = []
      // Show the recalculating banner briefly
      var self = this
      this.resuming = true
      setTimeout(function () {
        self.resuming = false
      }, 5000)
    },

    // Called by XolasTable @total-changed — captures the total matched record count
    // for the "select all N matching" banner logic and counter.
    onTotalChanged: function (total) {
      this.matchTotal = total
    },

    // Enter select-all mode: the banner acts as the source of truth.
    // excludedIds reset so the autoSelectAll prop starts with a clean state.
    enterSelectAllMatching: function () {
      this.excludedIds = []
      this.selectAllMatching = true
    },

    // Exit select-all mode: clear logical selection + table checkboxes.
    // Also clears excludedIds so no stale exclusions survive a re-enter.
    clearSelectAllMatching: function () {
      this.selectAllMatching = false
      this.showSelectAllBanner = false
      this.selectedRows = []
      this.excludedIds = []
      if (this.$refs.resultsTable) {
        this.$refs.resultsTable.clearSelection()
      }
    },

    // XOL-321 Phase B — Reset exclusions: re-include all excluded rows, stay
    // in select-all mode. The excludedIds=[] assignment causes the XolasTable
    // :excluded-ids watcher to fire applyAutoSelection(), re-checking the
    // previously-excluded rows on the current page.
    resetExclusions: function () {
      this.excludedIds = []
    },

    // Filter rate changed: clear version, load versions list (no auto-pin for filter).
    onFilterRateChange: function (rateId) {
      this.filterVersionId = null
      this.filterRateVersions = []
      if (!rateId) return
      var self = this
      self.loadingFilterVersions = true
      this.fetchData('rates/' + rateId + '/versions', function (data) {
        self.filterRateVersions = Array.isArray(data) ? data : []
        self.loadingFilterVersions = false
      }, 'rate versions', function () {
        // error: leave empty (Any version fallback)
        self.filterRateVersions = []
        self.filterVersionId = null
        self.loadingFilterVersions = false
      })
    },

    onFilterVersionInput: function (val) {
      this.filterVersionId = val
    },

    // Open the tier-rates info dialog for a given result row.
    // Rows without a rate are inert (el-link only rendered when rate present).
    openRateVersion: function (row) {
      this.activeRow = row
      this.dialogVisible = true
    },

    // Called when the dialog closes (header X / backdrop / Esc / Close button).
    // Null activeRow so the child unmounts cleanly.
    onDialogClose: function () {
      this.activeRow = null
    }
  },

  watch: {
    // When telco changes, clear rate and version filters (rate picker is org-scoped)
    telcoId: function (val, oldVal) {
      if (val === oldVal) return
      this.filterRateId = null
      this.filterVersionId = null
      this.filterRateVersions = []
    },

    // When filter rate changes, load its versions
    filterRateId: function (val, oldVal) {
      if (val === oldVal) return
      this.onFilterRateChange(val)
    },

    // Clear select-all mode when the user edits filters after a search.
    // A changed filter makes the stale logical selection invalid — force them
    // to re-search before re-engaging select-all. Only fires after a search
    // has been run (searched=true) because before that there's nothing to clear.
    searchParams: function () {
      if (this.searched && (this.selectAllMatching || this.showSelectAllBanner)) {
        this.selectAllMatching = false
        this.showSelectAllBanner = false
        this.excludedIds = []
      }
    }
  },

  data: function () {
    return {
      // Filter fields
      telcoId: null,
      site: '',
      structureTypes: [],
      heightMin: null,
      heightMax: null,
      handoverRange: [],
      startedRange: [],
      // Structure type picker — populated in created() from GET /api/structures/names.
      // Degrades gracefully (empty picker) until that endpoint is live on brave-kestrel.
      structureTypeOptions: [],
      loadingStructureTypes: false,
      // Rate/version filter fields (Phase 2)
      filterRateId: null,
      filterVersionId: null,
      filterRateVersions: [],
      // Override-set filter: checked → only manually-overridden licensees (rate_locked=1).
      rateOverridden: false,
      // No-rate filter: checked → only unrated licensees (no_rate=1 → rate_id IS NULL).
      noRate: false,
      loadingFilterVersions: false,
      // State
      searched: false,
      searchKey: 0,
      // Bumped on Reset to remount the telco select (it doesn't watch `value`).
      telcoSelectKey: 0,
      resuming: false,
      selectedRows: [],
      // Phase B — "select all N matching" state (XOL-321)
      // matchTotal: total records matched by the current search (from @total-changed)
      // showSelectAllBanner: offer banner (page fully selected + more pages exist)
      // selectAllMatching: active mode — apply routes to POST /licensees/bulk-rate filter-mode
      // excludedIds: ids of licensees de-selected while in select-all mode (XOL-321 Phase B)
      matchTotal: 0,
      showSelectAllBanner: false,
      selectAllMatching: false,
      excludedIds: [],
      // Tier-rates dialog state
      dialogVisible: false,
      activeRow: null,
      // Table columns
      fields: [
        {
          name: '__slot:site',
          title: 'Site',
          minWidth: 150
        },
        {
          name: 'organisation.name',
          title: 'Telco',
          minWidth: 140
        },
        {
          name: 'structureType',
          title: 'Structure',
          minWidth: 120,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down'
        },
        {
          name: 'structureHeight',
          title: 'Height',
          width: 100,
          align: 'right',
          callback: this.formatHeight,
          titleClass: 'hidden-sm-and-down',
          dataClass: 'hidden-sm-and-down'
        },
        {
          name: 'started',
          title: 'Started',
          width: 120,
          callback: this.formatDate,
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down'
        },
        {
          name: 'handover',
          title: 'Handover',
          width: 120,
          callback: this.formatDate,
          titleClass: 'hidden-md-and-down',
          dataClass: 'hidden-md-and-down'
        },
        {
          name: '__slot:currentRate',
          title: 'Current rate',
          minWidth: 140
        }
      ]
    }
  }
}
</script>

<style scoped>
/* The Telco select goes through SelectSharer → SelectOrg, which in :no-form mode
   renders a stray <i>{{label}}&nbsp;</i> before the dropdown and doesn't force the
   inner el-select to full width — leaving the input slightly indented and narrower
   than the SelectSharerRates/SelectRateVersions cells (both self-apply width:100%).
   Normalise it here so all row-1 inputs align. Deep selectors reach the child
   component's internals; the `> div > i` path matches only the stray label node,
   not el-select's caret/clear icons (those sit inside a <span>). */
.bulk-rate-change__telco >>> .el-form-item__content > div > i {
  display: none;
}

.bulk-rate-change__telco >>> .el-select {
  width: 100%;
}

/* Separate the search filters from the apply bar / select-all banner / results
   below — without this they butt right up against the Search/Reset row. */
.bulk-rate-change__form {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}

.bulk-rate-change__rate-name {
  line-height: 1.3;
}

.bulk-rate-change__rate-version {
  margin-top: 2px;
}

.bulk-rate-change__rate-muted {
  color: #909399;
  font-size: 12px;
}

/* "Rate overridden" label sits above the override amount; keep it small and
   compact so the fee below reads as the primary value. */
.bulk-rate-change__override-tag-row {
  margin-bottom: 2px;
}

.bulk-rate-change__override-tag {
  height: 16px;
  padding: 0 4px;
  font-size: 10px;
  line-height: 15px;
}

.bulk-rate-change__range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bulk-rate-change__range-sep {
  white-space: nowrap;
  color: #909399;
  font-size: 13px;
}

.bulk-rate-change__empty {
  padding: 32px 0;
  text-align: center;
}

.bulk-rate-change__empty-text {
  font-size: 15px;
  margin-bottom: 4px;
}

.bulk-rate-change__empty-muted {
  color: #909399;
  font-size: 13px;
}

/* Pre-search prompt icon — matches SitesTable's hand-rolled empty state. */
.bulk-rate-change__empty-icon {
  display: block;
  font-size: 48px;
  color: #DCDFE6;
  margin-bottom: 8px;
}

.audit-site-cell__id {
  display: block;
  font-weight: 600;
}

.audit-site-cell__name {
  display: block;
  color: #909399;
  font-size: 12px;
}

/* Trailing icon inside the rate-name el-link */
.bulk-rate-change__rate-icon {
  margin-left: 4px;
  font-size: 12px;
}

/* Dialog body: reserve height for loading spinner; cap tall grids with scroll */
.bulk-rate-change__dialog-body {
  min-height: 220px;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: auto;
}

/* Banner dividers between the summary and the text-button actions.
   Muted middot so dividers recede visually; nowrap keeps "· Reset exclusions"
   together so the dot never orphans on a line-break (sm/xs wrap). */
.bulk-rate-change__banner-sep {
  color: #c0c4cc;
  white-space: nowrap;
}
</style>
